import React from 'react';
import { ArrowRight, MapPin, Shield, Scale } from 'lucide-react';
import { Link } from 'react-router-dom';
import { companyInfo } from '../mockData';

const Hero = () => {
  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToServices = () => {
    const element = document.getElementById('services');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section style={{
      minHeight: '100vh',
      background: 'var(--bg-page)',
      display: 'flex',
      alignItems: 'center',
      position: 'relative',
      paddingTop: '120px',
      paddingBottom: '80px',
      overflow: 'hidden'
    }}>
      {/* Background Pattern */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        opacity: 0.05,
        backgroundImage: 'radial-gradient(circle at 25% 25%, var(--brand-primary) 2px, transparent 2px)',
        backgroundSize: '60px 60px'
      }} />

      <div className="container">
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: '3rem',
          alignItems: 'center',
          position: 'relative',
          zIndex: 1
        }}>
          {/* Hero Content */}
          <div style={{ textAlign: 'center', maxWidth: '900px', margin: '0 auto' }}>
            {/* Location Badge */}
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              backgroundColor: 'var(--bg-card)',
              padding: '0.75rem 1.5rem',
              borderRadius: '50px',
              marginBottom: '2rem',
              border: '1px solid var(--border-medium)'
            }}>
              <MapPin size={16} color="var(--brand-primary)" />
              <span className="caption" style={{ color: 'var(--text-primary)' }}>
                {companyInfo.location} • {companyInfo.coverage}
              </span>
            </div>

            {/* Main Headline */}
            <h1 className="brand-display" style={{
              marginBottom: '1rem',
              maxWidth: '800px',
              margin: '0 auto 1rem auto'
            }}>
              Attorney Service
            </h1>
            <h2 className="heading-1" style={{
              marginBottom: '1rem',
              maxWidth: '700px',
              margin: '0 auto 1rem auto',
              color: 'var(--text-secondary)',
              fontWeight: '500'
            }}>
              Legal Document Specialist
            </h2>

            {/* Services List */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '1.5rem',
              marginBottom: '2rem',
              maxWidth: '700px',
              margin: '0 auto 2rem auto'
            }}>
              {[
                'Process Serving',
                'Court Filing', 
                'E-Filing',
                'County Recording',
                'Skip Tracing',
                'Document Retrieval'
              ].map((service, index) => (
                <div key={index} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 1rem',
                  backgroundColor: 'rgba(44, 90, 160, 0.1)',
                  borderRadius: '25px',
                  border: '1px solid rgba(44, 90, 160, 0.2)'
                }}>
                  <div style={{
                    width: '6px',
                    height: '6px',
                    backgroundColor: 'var(--brand-primary)',
                    borderRadius: '50%'
                  }} />
                  <span style={{
                    color: 'var(--text-primary)',
                    fontSize: '0.95rem',
                    fontWeight: '500'
                  }}>
                    {service}
                  </span>
                </div>
              ))}
            </div>

            {/* Subheadline */}
            <p className="body-large" style={{
              marginBottom: '2rem',
              maxWidth: '700px',
              margin: '0 auto 2rem auto',
              color: 'var(--text-secondary)'
            }}>
              Highly effective professionals trained and equipped to manage high-volume process serving, court filing, county recording, and skip traces for legal professionals. We also serve non-legal businesses and individuals with the same extremely high standard of performance and expertise.
            </p>

            {/* Trust Indicators */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '2rem',
              flexWrap: 'wrap',
              marginBottom: '3rem'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <Shield size={20} color="var(--brand-primary)" />
                <span className="body-small" style={{ color: 'var(--text-primary)' }}>
                  Registered Process Servers
                </span>
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <MapPin size={20} color="var(--brand-primary)" />
                <span className="body-small" style={{ color: 'var(--text-primary)' }}>
                  Statewide Coverage
                </span>
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <ArrowRight size={20} color="var(--brand-primary)" />
                <span className="body-small" style={{ color: 'var(--text-primary)' }}>
                  Fast Service
                </span>
              </div>
            </div>

            {/* Main CTA Buttons - Above the Fold */}
            <div style={{
              display: 'flex',
              gap: '1.5rem',
              justifyContent: 'center',
              flexWrap: 'wrap',
              marginBottom: '2rem',
              alignItems: 'center'
            }}>
              {/* Attorney Service - Primary Button (2x size) */}
              <Link 
                to="/attorney-service"
                className="btn-primary"
                style={{
                  fontSize: '1.3rem',
                  padding: '1.5rem 4rem',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  textDecoration: 'none',
                  boxShadow: '0 6px 20px rgba(44, 90, 160, 0.4)',
                  transition: 'all 0.3s ease',
                  fontWeight: '700',
                  background: 'linear-gradient(135deg, var(--brand-primary) 0%, #1e40af 100%)',
                  transform: 'scale(1)',
                  border: '2px solid var(--brand-primary)'
                }}
              >
                <Scale size={24} />
                Attorney Services
                <ArrowRight size={24} />
              </Link>
              
              {/* Secondary buttons */}
              <div style={{
                display: 'flex',
                gap: '1rem',
                flexWrap: 'wrap',
                justifyContent: 'center'
              }}>
                <a 
                  href="https://legaldocspecialist.legalconnect.com/registration"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary"
                  style={{
                    fontSize: '1rem',
                    padding: '0.9rem 2rem',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    textDecoration: 'none',
                    fontWeight: '600'
                  }}
                >
                  Create Account
                </a>
                
                <a 
                  href="https://legaldocspecialist.legalconnect.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary"
                  style={{
                    fontSize: '1rem',
                    padding: '0.9rem 2rem',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    textDecoration: 'none',
                    fontWeight: '600'
                  }}
                >
                  Log In
                </a>
                
                <Link 
                  to="/place-order"
                  className="btn-secondary"
                  style={{
                    fontSize: '1rem',
                    padding: '0.9rem 2rem',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    textDecoration: 'none',
                    fontWeight: '600'
                  }}
                >
                  Place Order
                  <ArrowRight size={18} />
                </Link>
              </div>
            </div>

            {/* Secondary CTA - Emergency Service */}
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              backgroundColor: '#ef4444',
              color: 'white',
              padding: '0.75rem 1.5rem',
              borderRadius: '50px',
              fontSize: '0.9rem',
              fontWeight: '600',
              marginBottom: '3rem',
              animation: 'pulse 2s infinite'
            }}>
              <div style={{
                width: '8px',
                height: '8px',
                backgroundColor: 'white',
                borderRadius: '50%',
                animation: 'blink 1s infinite'
              }} />
              Same-Day Emergency Service Available
            </div>

            {/* Trust Indicators */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '2rem',
              marginTop: '2rem',
              flexWrap: 'wrap'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '1.2rem', fontWeight: '700', color: 'var(--brand-primary)' }}>
                  22+ Years
                </div>
                <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                  Experience
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '1.2rem', fontWeight: '700', color: 'var(--brand-primary)' }}>
                  Same Day
                </div>
                <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                  Service Available
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '1.2rem', fontWeight: '700', color: 'var(--brand-primary)' }}>
                  All 58 Counties
                </div>
                <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                  California Coverage
                </div>
              </div>
            </div>

            {/* Contact Info */}
            <div style={{
              marginTop: '3rem',
              padding: '2rem',
              backgroundColor: 'var(--bg-card)',
              borderRadius: '12px',
              border: '1px solid var(--border-medium)',
              display: 'inline-block'
            }}>
              <p className="body-small" style={{ 
                color: 'var(--text-secondary)',
                marginBottom: '0.5rem' 
              }}>
                Need immediate assistance?
              </p>
              <p className="body-medium" style={{ 
                color: 'var(--text-primary)',
                fontWeight: '600'
              }}>
                Email us at {companyInfo.email}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Decorative Elements */}
      <div style={{
        position: 'absolute',
        top: '20%',
        right: '-10%',
        width: '300px',
        height: '300px',
        background: 'radial-gradient(circle, rgba(217, 251, 6, 0.1) 0%, transparent 70%)',
        borderRadius: '50%',
        filter: 'blur(100px)'
      }} />
      
      <div style={{
        position: 'absolute',
        bottom: '30%',
        left: '-10%',
        width: '200px',
        height: '200px',
        background: 'radial-gradient(circle, rgba(63, 72, 22, 0.2) 0%, transparent 70%)',
        borderRadius: '50%',
        filter: 'blur(80px)'
      }} />

      <style>{`
        @media (max-width: 767px) {
          .brand-display {
            font-size: clamp(2rem, 4vw, 3rem) !important;
          }
          .body-large {
            font-size: 1.2rem !important;
          }
        }
      `}</style>
    </section>
  );
};

export default Hero;